import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@beautyapp/core';
import {
  Customer360Bundle,
  CustomerSummary,
  DayOfWeekBucket,
  HourBucket,
  MonthBucket,
  EmployeeBucket,
  ServiceBucket,
  SpendingResponse,
  TimelineEntry,
  CommunicationEntry,
} from '../models/customer-360.types';

/**
 * Talks to the `reports/customers/:userId/*` endpoints added to
 * `ReportsModule` on the backend. The drawer opens with one `/360`
 * bundle call, then panels can refresh themselves through the
 * per-endpoint methods when the user changes the date range.
 */
@Injectable({ providedIn: 'root' })
export class Customer360Service {
  private api = inject(ApiService);

  get360(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<Customer360Bundle> {
    return this.api.get<Customer360Bundle>(
      `reports/customers/${userId}/360`,
      params,
    );
  }

  getSummary(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<CustomerSummary> {
    return this.api.get<CustomerSummary>(
      `reports/customers/${userId}/summary`,
      params,
    );
  }

  getByDayOfWeek(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<DayOfWeekBucket[]> {
    return this.api.get<DayOfWeekBucket[]>(
      `reports/customers/${userId}/by-day-of-week`,
      params,
    );
  }

  getByHourOfDay(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<HourBucket[]> {
    return this.api.get<HourBucket[]>(
      `reports/customers/${userId}/by-hour-of-day`,
      params,
    );
  }

  getByMonth(
    userId: string,
    params?: { months?: number },
  ): Observable<MonthBucket[]> {
    return this.api.get<MonthBucket[]>(
      `reports/customers/${userId}/by-month`,
      params,
    );
  }

  getByEmployee(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<EmployeeBucket[]> {
    return this.api.get<EmployeeBucket[]>(
      `reports/customers/${userId}/by-employee`,
      params,
    );
  }

  getSpending(
    userId: string,
    params?: { startDate?: string; endDate?: string; granularity?: 'day' | 'week' | 'month' },
  ): Observable<SpendingResponse> {
    return this.api.get<SpendingResponse>(
      `reports/customers/${userId}/spending`,
      params,
    );
  }

  getServices(
    userId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<ServiceBucket[]> {
    return this.api.get<ServiceBucket[]>(
      `reports/customers/${userId}/services`,
      params,
    );
  }

  getTimeline(
    userId: string,
    params?: { limit?: number; before?: string },
  ): Observable<TimelineEntry[]> {
    return this.api.get<TimelineEntry[]>(
      `reports/customers/${userId}/timeline`,
      params,
    );
  }

  getCommunications(
    userId: string,
    params?: { limit?: number },
  ): Observable<CommunicationEntry[]> {
    return this.api.get<CommunicationEntry[]>(
      `reports/customers/${userId}/communications`,
      params,
    );
  }
}
